import { useCallback, useEffect, useRef } from 'react'

export const useDelayedCallback = (
  timeoutMs: number,
  callback: () => Promise<unknown> | unknown,
) => {
  const callbackRef = useRef(callback)
  const canExecuteOnUnmountRef = useRef(false)
  const statusRef = useRef<'pending' | 'canceled' | 'executed'>('pending')

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const cancel = useCallback(() => {
    statusRef.current = 'canceled'
  }, [])

  const execute = useCallback(() => {
    if (statusRef.current !== 'pending') return

    statusRef.current = 'executed'
    void callbackRef.current()
  }, [])

  useEffect(() => {
    // React StrictMode can run an immediate mount cleanup in development.
    // Only execute on unmount after the effect has survived one event-loop tick.
    canExecuteOnUnmountRef.current = false
    const enableUnmountExecutionId = window.setTimeout(() => {
      canExecuteOnUnmountRef.current = true
    }, 0)
    const timeoutId = window.setTimeout(execute, timeoutMs)

    return () => {
      window.clearTimeout(enableUnmountExecutionId)
      window.clearTimeout(timeoutId)
      if (canExecuteOnUnmountRef.current) {
        execute()
      }
    }
  }, [execute, timeoutMs])

  return { cancel, execute }
}
